import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LABELS = ["", "Poor", "Fair", "Good", "Great", "Amazing!"];
const LABEL_COLORS = ["", "text-red-500", "text-orange-500", "text-yellow-500", "text-lime-500", "text-green-500"];

export default function GuestReview() {
  const { bookingId } = useParams();
  const [info, setInfo]       = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [rating, setRating]   = useState(0);
  const [hovered, setHovered] = useState(0);
  const [text, setText]       = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [submitErr, setSubmitErr]   = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/review-info/${bookingId}`)
      .then(r => setInfo(r.data))
      .catch(e => setLoadErr(e?.response?.data?.detail || "This review link is invalid or has already been used."));
  }, [bookingId]);

  const handleSubmit = async () => {
    if (!rating) return;
    setSubmitting(true);
    setSubmitErr(null);
    try {
      await axios.post(`${API}/public/review/${bookingId}`, { rating, review_text: text });
      setSubmitted(true);
    } catch (e) {
      setSubmitErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  if (loadErr) return (
    <div className="min-h-screen flex items-center justify-center bg-amber-50 p-6">
      <div className="text-center max-w-sm">
        <div className="text-6xl mb-4">🙁</div>
        <h2 className="text-lg font-semibold text-slate-700 mb-2">Link unavailable</h2>
        <p className="text-slate-500 text-sm">{loadErr}</p>
      </div>
    </div>
  );

  if (!info) return (
    <div className="min-h-screen flex items-center justify-center bg-amber-50">
      <div className="w-9 h-9 rounded-full border-4 border-amber-400 border-t-transparent animate-spin" />
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center bg-amber-50 p-6">
      <div className="text-center max-w-sm">
        <div className="text-7xl mb-5">🎉</div>
        <h1 className="text-2xl font-bold text-slate-800 mb-2">
          Thank you, {info.guest_name.split(" ")[0]}!
        </h1>
        <p className="text-slate-500 text-sm leading-relaxed">
          Your review has been shared with the host. We hope to welcome you back soon!
        </p>
        <div className="mt-8 text-xs text-slate-400">
          Powered by <span className="font-bold text-amber-500">HostFolio</span>
        </div>
      </div>
    </div>
  );

  const active = hovered || rating;

  return (
    <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl w-full max-w-md overflow-hidden">

        {/* Header band */}
        <div className="bg-amber-400 px-8 pt-8 pb-10 text-white text-center">
          <p className="text-[11px] font-semibold uppercase tracking-widest opacity-80 mb-1">Powered by HostFolio</p>
          <h1 className="text-2xl font-bold mb-1">How was your stay?</h1>
          <p className="text-sm opacity-90 font-medium">{info.property_name}</p>
          <p className="text-xs opacity-70 mt-0.5">
            {info.check_in} → {info.check_out}
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-7 -mt-3 bg-white rounded-t-3xl relative z-10">

          <p className="text-sm text-slate-500 text-center mb-5">
            Hi <strong className="text-slate-700">{info.guest_name.split(" ")[0]}</strong>, tap a star to rate your experience
          </p>

          {/* Star picker */}
          <div className="flex justify-center gap-2 mb-2">
            {[1, 2, 3, 4, 5].map(s => (
              <button
                key={s}
                onClick={() => setRating(s)}
                onMouseEnter={() => setHovered(s)}
                onMouseLeave={() => setHovered(0)}
                className="text-4xl transition-transform duration-100 hover:scale-125 active:scale-110 select-none"
                style={{ color: s <= active ? "#f59e0b" : "#e2e8f0" }}
              >
                ★
              </button>
            ))}
          </div>

          {/* Rating label */}
          <div className="h-5 text-center mb-5">
            {active > 0 && (
              <span className={`text-sm font-bold ${LABEL_COLORS[active]}`}>
                {LABELS[active]}
              </span>
            )}
          </div>

          {/* Text area */}
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="What did you love? Anything we could improve? (optional)"
            rows={4}
            className="w-full border border-slate-200 rounded-2xl p-4 text-sm text-slate-700 placeholder:text-slate-300 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 resize-none transition"
          />

          {submitErr && (
            <p className="text-xs text-red-500 mt-2 text-center">{submitErr}</p>
          )}

          <button
            onClick={handleSubmit}
            disabled={!rating || submitting}
            className="w-full mt-4 bg-amber-400 hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl text-sm transition-colors"
          >
            {submitting ? "Submitting…" : "Submit Review"}
          </button>

          <p className="text-center text-[11px] text-slate-300 mt-4">
            Your honest feedback helps improve future stays.
          </p>
        </div>
      </div>
    </div>
  );
}
